// src/lib/repairs/useRepairs.tsx
//
// Repairs / service-tickets module (M19 + Phase 5 wp11).
// React Context + useReducer, persisted to localStorage under
// 'bench-repairs-v1'. Schema-guarded load. Five seed tickets
// across the three demo customers so the page is non-empty out
// of the box.
//
// Public hooks:
//   useRepairs() -> { tickets, getById, create, advance, reset }

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import {
  type RepairStatus,
  type RepairTicket,
  type RepairTimelineEntry,
  type RepairWarranty,
} from "./types";

const STORAGE_KEY = "bench-repairs-v1";

// ---------- storage ----------

function loadFromStorage(): RepairTicket[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (t) =>
          typeof t === "object" &&
          t &&
          "id" in t &&
          "status" in t &&
          "timeline" in t,
      )
    ) {
      return parsed as RepairTicket[];
    }
    return null;
  } catch {
    return null;
  }
}

function saveToStorage(rows: RepairTicket[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // ignore quota / private-mode
  }
}

// ---------- minting ----------

function nextTicketId(rows: RepairTicket[]): string {
  let max = 0;
  for (const r of rows) {
    const m = /^REP-(\d+)$/.exec(r.id);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `REP-${String(max + 1).padStart(4, "0")}`;
}

function isoMinusDays(days: number, hour = 9): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

// ---------- seed ----------

const SEED: RepairTicket[] = [
  {
    id: "REP-0001",
    customerId: "C-1001",
    customerName: "Studio Norden",
    sku: "MADV-ENVY",
    productName: "madVR Envy Video Processor",
    serialNumber: "MADV-00137",
    description:
      "Unit boots but no output on HDMI 2; display detects source briefly then drops the signal. Issue reproduced with two different cables.",
    warranty: "in-warranty",
    status: "vendor-routing",
    vendor: "madVR Labs",
    internalNotes:
      "Open vendor RA first; ship unit to madVR Repair Center (DE) with photos of the firmware diagnostic screen attached.",
    timeline: [
      {
        ts: isoMinusDays(8),
        status: "open",
        actor: "system",
        note: "Customer opened a service ticket via the portal.",
      },
      {
        ts: isoMinusDays(7, 10),
        status: "triage",
        actor: "agent",
        note: "Agent H checked order history: unit bought 7 months ago, vendor warranty active.",
      },
      {
        ts: isoMinusDays(7, 11),
        status: "vendor-routing",
        actor: "human",
        note: "Service desk opened vendor RA with madVR Labs, attached diagnostic screenshots.",
      },
    ],
  },
  {
    id: "REP-0002",
    customerId: "C-1002",
    customerName: "Cinema Royale",
    sourceOrderId: "ORD-2003",
    sku: "DAC-REF2",
    productName: "DAC Reference 2",
    serialNumber: "DACR-04211",
    description:
      "Channel B drops to silence intermittently. Issue started after a firmware update pushed on 2026-06-30.",
    warranty: "out-of-warranty",
    status: "triage",
    internalNotes: "",
    timeline: [
      {
        ts: isoMinusDays(3),
        status: "open",
        actor: "system",
        note: "Customer opened a service ticket via the portal.",
      },
      {
        ts: isoMinusDays(2, 9),
        status: "triage",
        actor: "agent",
        note: "Warranty check: unit outside vendor warranty window. Quote-ready repair offered.",
      },
    ],
  },
  {
    id: "REP-0003",
    customerId: "C-1003",
    customerName: "SoundHouse",
    sourceOrderId: "ORD-2010",
    sku: "ROLLA-85PRO",
    productName: "RollaTv 85 Pro Display",
    description:
      "Customer reports a faint horizontal banding on the lower-right quadrant. Persistent across HDMI inputs.",
    warranty: "extended-warranty",
    status: "in-repair",
    vendor: "RollaTv",
    internalNotes:
      "Bench BENCHMARK extended warranty in force (3 years). In-house technician booked for Friday.",
    timeline: [
      {
        ts: isoMinusDays(14),
        status: "open",
        actor: "system",
        note: "Customer opened a service ticket via the portal.",
      },
      {
        ts: isoMinusDays(13, 11),
        status: "triage",
        actor: "agent",
        note: "Warranty check: extended warranty in force.",
      },
      {
        ts: isoMinusDays(12, 9),
        status: "vendor-routing",
        actor: "human",
        note: "Triage decision: in-house repair (cheaper than shipping back).",
      },
      {
        ts: isoMinusDays(7),
        status: "in-repair",
        actor: "human",
        note: "Technician accepted the work order; estimated 2 days.",
      },
    ],
  },
  {
    id: "REP-0004",
    customerId: "C-1001",
    customerName: "Studio Norden",
    sourceOrderId: "ORD-2008",
    sku: "MAGT-15",
    productName: "MAG Theatron 15 (L/C/R Speaker)",
    description: "Tweeter cone detached on one speaker, presumably shipping damage.",
    warranty: "in-warranty",
    status: "return-shipped",
    vendor: "MAG Theatron",
    glsTrackingNumber: "GLS-3400921901",
    internalNotes: "Vendor shipped replacement cone assembly. Returning customer with the new tracking number.",
    timeline: [
      {
        ts: isoMinusDays(11),
        status: "open",
        actor: "system",
        note: "Customer opened a service ticket via the portal.",
      },
      {
        ts: isoMinusDays(10, 9),
        status: "triage",
        actor: "agent",
        note: "Photo evidence reviewed; in-warranty damage claim accepted.",
      },
      {
        ts: isoMinusDays(10, 14),
        status: "vendor-routing",
        actor: "human",
        note: "Replacement parts dispatched by MAG Theatron.",
      },
      {
        ts: isoMinusDays(5),
        status: "in-repair",
        actor: "human",
        note: "Bench service swapped the cone assembly; unit ready to ship back.",
      },
      {
        ts: isoMinusDays(1),
        status: "return-shipped",
        actor: "human",
        note: "GLS return label printed; shipped.",
      },
    ],
  },
  {
    id: "REP-0005",
    customerId: "C-1002",
    customerName: "Cinema Royale",
    sourceOrderId: "ORD-2001",
    sku: "RV-PRO-8K",
    productName: "R_Volution Pro 8K Media Player",
    description:
      "HDD slot cover loose. Cosmetic only; no functional impact. Already 12 months into the warranty.",
    warranty: "in-warranty",
    status: "closed",
    vendor: "R_Volution",
    internalNotes: "Vendor sent replacement top cover. Customer installed in-house; ticket closed by service-desk lead.",
    timeline: [
      {
        ts: isoMinusDays(40),
        status: "open",
        actor: "system",
        note: "Customer opened a service ticket via the portal.",
      },
      {
        ts: isoMinusDays(39, 9),
        status: "triage",
        actor: "agent",
        note: "Warranty in force.",
      },
      {
        ts: isoMinusDays(38, 11),
        status: "vendor-routing",
        actor: "human",
        note: "R_Volution dispatched a replacement top cover (no-bench repair).",
      },
      {
        ts: isoMinusDays(28),
        status: "in-repair",
        actor: "human",
        note: "Customer self-installed; awaiting close-out.",
      },
      {
        ts: isoMinusDays(21),
        status: "return-shipped",
        actor: "human",
        note: "Self-install case; no physical return label issued.",
      },
      {
        ts: isoMinusDays(18),
        status: "closed",
        actor: "human",
        note: "Confirmed with customer that the issue is resolved. Closed.",
      },
    ],
  },
];

// ---------- reducer ----------

export type RepairAction =
  | { type: "replaceAll"; payload: RepairTicket[] }
  | { type: "reset" }
  | { type: "create"; payload: RepairTicket }
  | {
      type: "advance";
      payload: {
        id: string;
        next: RepairStatus;
        actor: RepairTimelineEntry["actor"];
        note: string;
        setters?: Partial<RepairTicket> | undefined;
      };
    };

function reducer(rows: RepairTicket[], action: RepairAction): RepairTicket[] {
  switch (action.type) {
    case "replaceAll":
      return action.payload;
    case "reset":
      return SEED;
    case "create":
      return [action.payload, ...rows];
    case "advance":
      return rows.map((r) => {
        if (r.id !== action.payload.id) return r;
        const entry: RepairTimelineEntry = {
          ts: new Date().toISOString(),
          status: action.payload.next,
          actor: action.payload.actor,
          note: action.payload.note,
        };
        return {
          ...r,
          ...(action.payload.setters ?? {}),
          status: action.payload.next,
          timeline: [...r.timeline, entry],
        };
      });
  }
}

// ---------- context ----------

export interface RepairsContextValue {
  tickets: RepairTicket[];
  getById: (id: string) => RepairTicket | undefined;
  create: (
    input: Omit<RepairTicket, "id" | "status" | "timeline">,
  ) => RepairTicket;
  advance: (
    id: string,
    next: RepairStatus,
    actor: RepairTimelineEntry["actor"],
    note: string,
    setters?: Partial<RepairTicket>,
  ) => void;
  reset: () => void;
}

const RepairsContext = createContext<RepairsContextValue | null>(null);

export function RepairsProvider({ children }: { children: ReactNode }) {
  const [rows, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadFromStorage() ?? SEED,
  );

  useEffect(() => {
    saveToStorage(rows);
  }, [rows]);

  const value = useMemo<RepairsContextValue>(
    () => ({
      tickets: rows,
      getById: (id: string) => rows.find((r: RepairTicket) => r.id === id),
      create: (input) => {
        const created: RepairTicket = {
          id: nextTicketId(rows),
          status: "open",
          timeline: [
            {
              ts: new Date().toISOString(),
              status: "open",
              actor: "system",
              note: "Customer opened a service ticket via the portal.",
            },
          ],
          ...input,
        };
        dispatch({ type: "create", payload: created });
        return created;
      },
      advance: (id, next, actor, note, setters) =>
        dispatch({
          type: "advance",
          payload: { id, next, actor, note, setters },
        }),
      reset: () => dispatch({ type: "reset" }),
    }),
    [rows],
  );

  return (
    <RepairsContext.Provider value={value}>{children}</RepairsContext.Provider>
  );
}

export function useRepairs(): RepairsContextValue {
  const ctx = useContext(RepairsContext);
  if (!ctx) {
    throw new Error("useRepairs must be used inside a <RepairsProvider />");
  }
  return ctx;
}

// Test helper only.
export function _resetRepairsStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Suppress unused-import lint for RepairWarranty — kept in the public
// type export so consumers can name it.
void (null as RepairWarranty | null);
